import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { StaysNav, StaySearchBar, PropertyCard } from "@/components/travel/StaysShared";

export default function TravelHome() {
  const [meta, setMeta] = useState(null);
  const [live, setLive] = useState([]);
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    api.get("/travel/meta").then(({ data }) => setMeta(data)).catch(() => setMeta({ destinations: [], property_types: [] }));
    api.get("/travel/search").then(({ data }) => setLive(data.properties)).catch(() => {});
    api.get("/hotels").then(({ data }) => setSamples(data.slice(0, 4))).catch(() => {});
  }, []);
  return (
    <div data-testid="travel-home" className="min-h-screen bg-[#f7f5f0]">
      <StaysNav active="Stays" />
      <section className="relative overflow-hidden bg-ink text-white">
        <img src="https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=1800&q=80" alt="Jamaica coast" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <span className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider">SHOP NOW Travel &amp; Stays</span>
          <h1 className="font-clash font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-5 max-w-3xl">Book real stays across Jamaica — and the world.</h1>
          <p className="text-white/80 text-base md:text-lg mt-4 max-w-xl">Live availability and pricing from properties listed directly with Shop Now. No hidden fees — you see every tax and charge before you pay.</p>
          <div className="mt-8"><StaySearchBar /></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-5">
          <div><h2 className="font-display font-bold text-lg md:text-xl text-ink">Featured destination: Jamaica</h2><p className="text-sm text-muted-foreground">Kingston to Negril — pick a place to start.</p></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(meta?.destinations || []).map((d) => (
            <Link key={d.name} to={`/travel/search?destination=${encodeURIComponent(d.name)}`} className="group relative rounded-2xl overflow-hidden aspect-[4/5] bg-black/10" data-testid={`dest-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <img src={d.image} alt={d.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white"><div className="font-bold text-sm">{d.name}</div><div className="text-[11px] opacity-80">{d.parish}</div></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="flex items-end justify-between mb-5">
          <div><h2 className="font-display font-bold text-lg md:text-xl text-ink">Live on Shop Now</h2><p className="text-sm text-muted-foreground">{meta ? `${meta.live_properties} propert${meta.live_properties === 1 ? "y" : "ies"} listed directly by hosts — bookable now.` : ""}</p></div>
          <Link to="/travel/search" className="text-sm font-bold text-emerald inline-flex items-center gap-1">See all <ArrowRight size={14} /></Link>
        </div>
        {live.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="live-properties">{live.slice(0, 8).map((p) => <PropertyCard key={p.id} p={p} />)}</div>
        ) : (
          <div className="rounded-2xl border border-dashed border-black/15 p-10 text-center text-sm text-muted-foreground" data-testid="no-live-properties">No live properties yet. Be the first — <Link to="/travel/host" className="text-emerald font-bold">list your property</Link>.</div>
        )}
      </section>

      {samples.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="mb-4"><h2 className="font-display font-bold text-lg md:text-xl text-ink">Sample listings</h2><p className="text-sm text-muted-foreground">Illustrative examples only — <strong>not bookable</strong>. Real inventory appears above once a host lists it or a supplier is connected.</p></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {samples.map((h) => (
              <Link key={h.slug} to={`/hotels/${h.slug}`} className="relative bg-white rounded-2xl overflow-hidden border border-black/5 opacity-80" data-testid="sample-listing-card">
                <span className="absolute top-3 left-3 z-10 bg-amber-500 text-white text-[11px] font-bold rounded-full px-2.5 py-1">Sample listing – not bookable</span>
                <img src={h.image} alt={h.name} className="aspect-[4/3] w-full object-cover grayscale-[30%]" loading="lazy" />
                <div className="p-4"><div className="font-display font-bold text-ink">{h.name}</div><div className="text-xs text-muted-foreground">{h.parish} · {h.type}</div></div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid md:grid-cols-3 gap-4">
        {[[ShieldCheck, "Live availability, never guessed", "Every price and room count is checked against the property's calendar the moment you search and again before you pay."],
          [Sparkles, "Total price up front", "Nightly rate, taxes, resort and cleaning fees, and Shop Now's service fee are itemised before checkout."],
          [Building2, "List your property", "Hotels, villas, apartments and guest houses set their own rates, calendars and cancellation rules in the Host portal."]].map(([I, t, d]) => (
          <div key={t} className="bg-white rounded-2xl p-6 border border-black/5"><I className="text-emerald mb-3" size={22} /><div className="font-display font-bold text-ink">{t}</div><p className="text-sm text-muted-foreground mt-1">{d}</p></div>
        ))}
      </section>
      {meta && <p className="max-w-7xl mx-auto px-4 pb-8 text-[11px] text-muted-foreground" data-testid="travel-activation-note">{meta.activation_note}</p>}
    </div>
  );
}
